/** @jsxImportSource preact */
import { CARD_STYLES } from "../form/card-styles";
import { FORM_STYLES, formVars } from "../form/form-styles";
import { ICON_TILE_STYLES, IconTile } from "../contact/card-parts";
import type { WidgetTokens } from "../theme";
import {
  CardActions,
  CardIcon,
  type WidgetCardAction,
  type WidgetCardIcon,
} from "./card-actions";

export interface StatusCardProps {
  /** 一到两个整宽按钮，主答案在上（墨黑实心），次要的在下。 */
  actions?: WidgetCardAction[];
  /** 回放关闭主动播报。 */
  live?: boolean;
  description?: string;
  /** 标题上方一行小字，如 Something went wrong。 */
  eyebrow?: string;
  /** 图标，放在标题前的圆里。 */
  icon: WidgetCardIcon;
  title: string;
  tokens: WidgetTokens;
  /** default 井色圆；strong 危险色淡底圆加 alert 播报，用于出了问题、需要注意的状态。 */
  tone?: "default" | "strong";
}

export const STATUS_CARD_STYLES = `${ICON_TILE_STYLES}
.gn-statuscard-head { display:flex; align-items:flex-start; gap:10px; padding:2px 4px 0; }
.gn-statuscard-text { display:flex; flex-direction:column; justify-content:center; gap:2px; min-width:0; min-height:36px; }
.gn-statuscard-eyebrow { font-size:12px; font-weight:var(--gn-form-strong); color:var(--gn-form-quiet); }
.gn-statuscard[data-tone="strong"] .gn-statuscard-eyebrow { color:var(--gn-form-danger); }
.gn-statuscard-title { margin:0; font-size:15px; font-weight:700; letter-spacing:-.01em; line-height:1.25; }
.gn-statuscard-desc { font-size:12px; line-height:1.4; color:var(--gn-form-quiet); }
.gn-statuscard-actions { display:flex; flex-direction:column; gap:8px; }
`;

/**
 * 状态卡：图标圆、标题和说明排成一行头，下面是整宽胶囊按钮。没查到订单、暂时查不到、
 * 无法访问这笔订单这类整张卡就是一种状态的场景。
 */
export function StatusCard({
  actions,
  live = true,
  description,
  eyebrow,
  icon,
  title,
  tokens: t,
  tone = "default",
}: StatusCardProps) {
  return (
    <div
      class="gn-card gn-statuscard"
      data-tone={tone}
      role="group"
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${STATUS_CARD_STYLES}`}</style>
      <div
        class="gn-statuscard-head"
        role={live ? (tone === "strong" ? "alert" : "status") : undefined}
        aria-live={live && tone === "default" ? "polite" : undefined}
      >
        <IconTile tone={tone === "strong" ? "danger" : "soft"}>
          <CardIcon icon={icon} size={18} />
        </IconTile>
        <div class="gn-statuscard-text">
          {eyebrow && <div class="gn-statuscard-eyebrow">{eyebrow}</div>}
          <h3 class="gn-statuscard-title">{title}</h3>
          {description && <div class="gn-statuscard-desc">{description}</div>}
        </div>
      </div>
      {actions && actions.length > 0 && (
        <div class="gn-statuscard-actions">
          <CardActions actions={actions} tokens={t} />
        </div>
      )}
    </div>
  );
}
